import { memo, useEffect, useRef, useState } from "react"
import { Stack, Text, Center, Button, Container, Box, Input, HStack, IconButton, Card, Separator, Textarea } from "@chakra-ui/react"
import { useCart } from "contexts/cart"
import { formatCurrency } from "@/utilities/number"
import { useApp } from "contexts/app"
import { useForm } from "react-hook-form"
import { Field } from "@/components/ui/field"
import { useNavigate } from "react-router"
import ProfileService from "services/profile"
import BottomSheet from "components/bottom-sheet"
import { useStore } from "contexts/store"
import { LuPencil, LuPlus } from "react-icons/lu"

const profileService = new ProfileService();

const createTempId = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const MemberCheckout = () => {
    const { setProcessing, setPageTitle, showToast } = useApp();
    const { store } = useStore();
    const { cart, updateShippingAddress, completeCart, create, validateInventory } = useCart();
    const formRef = useRef();
    const navigate = useNavigate();
    const invoiceEnabled = !!store?.order_setting?.invoice_enabled;

    const [savedAddresses, setSavedAddresses] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [showAddressPicker, setShowAddressPicker] = useState(false);
    const [showNewForm, setShowNewForm] = useState(false);
    const [newAddr, setNewAddr] = useState({ full_name: "", phone_number: "", address: "" });
    const [savingNew, setSavingNew] = useState(false);

    useEffect(() => {
        setPageTitle("Thanh toán")
    }, [setPageTitle])

    const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm();
    const [showTaxForm, setShowTaxForm] = useState(false);

    const fillForm = (addr) => {
        const invoiceInfo = addr?.invoice_info || {};
        setValue("phone_number", addr?.phone_number || addr?.phone || "");
        setValue("full_name", addr?.full_name || "");
        setValue("address", addr?.address || "");
        setValue("invoice_company_name", invoiceInfo.company_name || "");
        setValue("invoice_tax_number", invoiceInfo.tax_number || "");
        setValue("invoice_address", invoiceInfo.address || "");
        setValue("invoice_email", invoiceInfo.email || "");
        setValue("invoice_note", invoiceInfo.note || "");
    };

    useEffect(() => {
        const load = async () => {
            try {
                const data = await profileService.get();
                const list = Array.isArray(data?.info?.shipping_address) ? data.info.shipping_address : [];
                const invoiceInfo = data?.info?.invoice_info || {};
                setSavedAddresses(list);
                const defaultAddr = list.find(x => x.is_default) || list[0];
                const addr = {
                    phone_number: defaultAddr?.phone_number || data?.info?.phone || "",
                    full_name: defaultAddr?.full_name || data?.info?.full_name || "",
                    address: defaultAddr?.address || data?.info?.address || "",
                    invoice_info: invoiceInfo,
                };
                setSelectedAddress(addr);
                fillForm(addr);
                // nếu chưa có địa chỉ nào thì mở sẵn form tạo mới
                if (list.length === 0) {
                    setNewAddr({ full_name: data?.info?.full_name || "", phone_number: data?.info?.phone || "", address: "" });
                    setShowNewForm(true);
                }
            } catch (_) { }
        };
        load();
    }, []);

    const pickAddress = (addr) => {
        const sel = { phone_number: addr.phone_number, full_name: addr.full_name, address: addr.address };
        setSelectedAddress(sel);
        fillForm(sel);
        setShowAddressPicker(false);
        setShowNewForm(false);
    };

    const saveNewAddress = async () => {
        if (!newAddr.address?.trim()) {
            showToast({ description: "Vui lòng nhập địa chỉ giao hàng", type: "error" });
            return;
        }
        setSavingNew(true);
        try {
            const currentData = await profileService.get();
            const currentList = Array.isArray(currentData?.info?.shipping_address) ? currentData.info.shipping_address : [];
            const newItem = {
                id: createTempId(),
                full_name: newAddr.full_name?.trim() || currentData?.info?.full_name || "",
                phone_number: newAddr.phone_number?.trim() || currentData?.info?.phone || "",
                address: newAddr.address.trim(),
                is_default: currentList.length === 0,
            };

            const profileFullName = String(
                currentData?.info?.full_name || newItem.full_name || selectedAddress?.full_name || ""
            ).trim();
            const profileAddress = String(
                currentData?.info?.address || newItem.address || selectedAddress?.address || ""
            ).trim();

            if (!profileFullName) {
                showToast({ description: "Vui lòng nhập họ tên người nhận", type: "error" });
                return;
            }

            if (!profileAddress) {
                showToast({ description: "Vui lòng nhập địa chỉ giao hàng", type: "error" });
                return;
            }

            const updatedList = [...currentList, newItem];
            await profileService.updateInfo({
                full_name: profileFullName,
                address: profileAddress,
                shipping_address_list: updatedList,
            });
            setSavedAddresses(updatedList);
            pickAddress(newItem);
            setNewAddr({ full_name: "", phone_number: "", address: "" });
            setShowNewForm(false);
        } catch (e) {
            showToast({ description: e?.message || "Lưu địa chỉ thất bại", type: "error" });
        } finally {
            setSavingNew(false);
        }
    };

    const onCartComplete = async (data) => {
        setProcessing(true);
        try {
            const invoiceInfo = invoiceEnabled ? {
                company_name: data.invoice_company_name?.trim?.() || "",
                tax_number: data.invoice_tax_number?.trim?.() || "",
                address: data.invoice_address?.trim?.() || "",
                email: data.invoice_email?.trim?.() || "",
                note: data.invoice_note?.trim?.() || "",
                tax_percent: Number(store?.order_setting?.tax_percent ?? 0),
            } : null;

            const hasInvoiceInfo = invoiceInfo && Object.values(invoiceInfo).some((value) => !!String(value).trim());
            await updateShippingAddress({
                phone_number: data.phone_number,
                full_name: data.full_name,
                address: data.address,
                note: data.note,
                invoice_info: hasInvoiceInfo ? invoiceInfo : null,
            });

            if (hasInvoiceInfo && localStorage.getItem("token")) {
                const currentProfile = await profileService.get();
                await profileService.updateInfo({
                    full_name: data.full_name || currentProfile?.info?.full_name || "",
                    address: data.address || currentProfile?.info?.address || "",
                    invoice_info: invoiceInfo,
                });
            }

            await validateInventory();
            var order = await completeCart();
            await create();
            setProcessing(false);
            showToast({ description: "Vui lòng thanh toán để hoàn tất đơn hàng", type: "success" });
            navigate(`/order-pay/${order.id}`);
        } catch (error) {
            if (error?.error_code === "voucher_not_allow") {
                showToast({
                    description: "Không thể áp dụng mã khuyến mãi. Vui lòng xoá mã khuyến mãi để tiếp tục",
                    type: "error",
                    duration: 6000,
                });
                navigate("/cart");
                setProcessing(false);
                return;
            }

            if (Array.isArray(error?.inventory_errors) && error.inventory_errors.length > 0) {
                showToast({
                    description: "Một số sản phẩm không đủ tồn kho hoặc đã hết hàng. Vui lòng cập nhật số lượng hoặc xoá món đã hết hàng.",
                    type: "error",
                    duration: 6000,
                });
                navigate("/cart");
                setProcessing(false);
                return;
            }

            setProcessing(false);
        }
    };

    const onSubmit = handleSubmit(async (data) => {
        if (invoiceEnabled && showTaxForm) {
            const missingFields = [];
            if (!String(data.invoice_company_name || "").trim()) missingFields.push("Tên công ty");
            if (!String(data.invoice_tax_number || "").trim()) missingFields.push("Mã số thuế");
            if (!String(data.invoice_address || "").trim()) missingFields.push("Địa chỉ hoá đơn");
            if (!String(data.invoice_email || "").trim()) missingFields.push("Email");

            if (missingFields.length > 0) {
                showToast({
                    description: `Vui lòng điền: ${missingFields.join(", ")}`,
                    type: "error",
                    duration: 5000,
                });
                return;
            }
        }

        await onCartComplete(data);
    });

    return (
        <div style={{ paddingBottom: 75 }}>
            <form ref={formRef} onSubmit={onSubmit}>
                <Stack gap="4" align="flex-start" maxW="sm">
                    <Text>Thông tin giao hàng</Text>

                    <Card.Root size="sm" variant="subtle" width="100%">
                        <Card.Body>
                            <HStack gap={3} align="start">
                                <Stack gap={1} flex={1}>
                                    <HStack gap={2} wrap="wrap">
                                        <Text fontWeight="semibold">{selectedAddress?.full_name || "-"}</Text>
                                        <Text color="fg.muted" fontSize="sm">{selectedAddress?.phone_number || ""}</Text>
                                    </HStack>
                                    <Text fontSize="sm">{selectedAddress?.address || "Chưa có địa chỉ giao hàng"}</Text>
                                </Stack>
                                <IconButton
                                    variant="ghost" size="sm"
                                    title="Đổi địa chỉ"
                                    onClick={() => {
                                        setShowTaxForm(false);
                                        setShowAddressPicker(true);
                                    }}
                                >
                                    <LuPencil />
                                </IconButton>
                            </HStack>
                        </Card.Body>
                    </Card.Root>

                    {/* Hidden inputs để react-hook-form ghi nhận khi submit */}
                    <div style={{ display: "none" }}>
                        <input {...register("phone_number")} />
                        <input {...register("full_name")} />
                        <input {...register("address")} />
                    </div>

                    <Field label="Ghi chú">
                        <Input {...register("note")} placeholder="Ghi chú thêm cho đơn hàng" />
                    </Field>

                    {invoiceEnabled && (
                        <Stack width="100%" gap={3}>
                            <Button type="button" variant="link" color="blue.600" onClick={() => {
                                setShowAddressPicker(false);
                                setShowNewForm(false);
                                setShowTaxForm(true);
                            }}>
                                {Object.values({
                                    company_name: watch?.("invoice_company_name") || "",
                                    tax_number: watch?.("invoice_tax_number") || "",
                                    address: watch?.("invoice_address") || "",
                                    email: watch?.("invoice_email") || "",
                                }).some((value) => String(value).trim()) ? "Sửa thông tin thuế" : "Thêm thông tin thuế"}
                            </Button>

                            <BottomSheet
                                open={showTaxForm}
                                onOpenChange={(open) => setShowTaxForm(open)}
                                title="Thông tin hoá đơn"
                            >
                                <Stack gap={3} pb={4}>
                                    <Field label="Tên công ty">
                                        <Input {...register("invoice_company_name")} placeholder="Tên công ty" />
                                    </Field>
                                    <Field label="Mã số thuế">
                                        <Input {...register("invoice_tax_number")} placeholder="Mã số thuế" />
                                    </Field>
                                    <Field label="Địa chỉ hoá đơn">
                                        <Input {...register("invoice_address")} placeholder="Địa chỉ hoá đơn" />
                                    </Field>
                                    <Field label="Email">
                                        <Input {...register("invoice_email")} placeholder="Email" />
                                    </Field>
                                    <Field label="Ghi chú hoá đơn">
                                        <Input {...register("invoice_note")} placeholder="Ghi chú cho hoá đơn" />
                                    </Field>
                                    <Button type="button" onClick={() => setShowTaxForm(false)}>Lưu thông tin thuế</Button>
                                </Stack>
                            </BottomSheet>
                        </Stack>
                    )}

                </Stack>
            </form>

            <BottomSheet
                open={showAddressPicker || showNewForm}
                onOpenChange={(open) => { if (!open) { setShowAddressPicker(false); setShowNewForm(false); } }}
                title={showNewForm && savedAddresses.length === 0 ? "Thêm địa chỉ giao hàng" : "Chọn địa chỉ giao hàng"}
                bodyPaddingX={3}
                bodyPaddingBottom={3}
            >
                <Stack gap={3} pb={4}>
                    {!showNewForm && savedAddresses.map((addr, index) => (
                        <Card.Root
                            key={addr.id || index}
                            size="sm"
                            variant="outline"
                            cursor="pointer"
                            onClick={() => pickAddress(addr)}
                        >
                            <Card.Body>
                                <HStack gap={3} align="start">
                                    <Stack gap={1} flex={1}>
                                        <HStack gap={2}>
                                            <Text fontWeight="semibold">{addr.full_name}</Text>
                                            {addr.is_default && (
                                                <Text color="green.600" fontSize="xs" fontWeight="medium">Mặc định</Text>
                                            )}
                                        </HStack>
                                        <Text fontSize="sm" color="fg.muted">{addr.phone_number}</Text>
                                        <Text fontSize="sm">{addr.address}</Text>
                                    </Stack>
                                </HStack>
                            </Card.Body>
                        </Card.Root>
                    ))}

                    {!showNewForm && (
                        <Button variant="outline" onClick={() => setShowNewForm(true)}>
                            <LuPlus />
                            Thêm địa chỉ mới
                        </Button>
                    )}

                    {showNewForm && (
                        <Stack gap={3}>
                            {savedAddresses.length > 0 && (
                                <Button variant="ghost" size="sm" onClick={() => setShowNewForm(false)}>← Danh sách địa chỉ</Button>
                            )}
                            <Stack gap={1}>
                                <Text color="fg.muted" fontSize="sm">Người nhận</Text>
                                <Input
                                    value={newAddr.full_name}
                                    onChange={(e) => setNewAddr(p => ({ ...p, full_name: e.target.value }))}
                                />
                            </Stack>
                            <Stack gap={1}>
                                <Text color="fg.muted" fontSize="sm">Số điện thoại nhận hàng</Text>
                                <Input
                                    value={newAddr.phone_number}
                                    onChange={(e) => setNewAddr(p => ({ ...p, phone_number: e.target.value }))}
                                />
                            </Stack>
                            <Stack gap={1}>
                                <Text color="fg.muted" fontSize="sm">Địa chỉ giao hàng</Text>
                                <Textarea
                                    value={newAddr.address}
                                    onChange={(e) => setNewAddr(p => ({ ...p, address: e.target.value }))}
                                    rows={3}
                                />
                            </Stack>
                            <Button onClick={saveNewAddress} loading={savingNew}>Lưu địa chỉ này</Button>
                        </Stack>
                    )}
                </Stack>
            </BottomSheet>

            <div style={{ position: "fixed", bottom: 0, left: 0, padding: 0, width: "100vw" }}>
                <Container maxW="md" style={{ padding: 0 }}>
                    <Box bg="bg" shadow="md">
                        <div style={{ display: "flex", padding: 16 }}>
                            <div style={{ flexGrow: 1 }}>
                                <Center height="100%">
                                    <Text fontWeight="semibold">{formatCurrency(cart?.total_amount)}</Text>
                                </Center>
                            </div>
                            <div>
                                <Button onClick={() => handleSubmit(onSubmit)()}>Hoàn tất</Button>
                            </div>
                        </div>
                    </Box>
                </Container>
            </div>
        </div>
    );
};

export default memo(MemberCheckout);
